import { cn } from '@nextui-org/react'
import React, { useCallback, useMemo, useState } from 'react'

export interface SidebarSearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Search value */
  value?: string
  /** Placeholder text */
  placeholder?: string
  /** Callback when value changes */
  onChange?: (value: string) => void
  /** Callback when cleared */
  onClear?: () => void
  /** Callback when closed */
  onClose?: () => void
  /** Callback when Enter key is pressed */
  onEnter?: () => void
  /** Additional CSS class name */
  className?: string
}

/**
 * SidebarSearchInput Component
 *
 * A search input component based on Input component, specifically for sidebar
 * Left side shows search icon, right side has close icon
 * Supports multiple state styles: focused, typing, full, filled
 */
export const SidebarSearchInput = React.forwardRef<HTMLInputElement, SidebarSearchInputProps>(
  (
    {
      value = '',
      placeholder = 'Search',
      onChange,
      onClear,
      onClose,
      onEnter,
      onFocus,
      onBlur,
      onKeyDown,
      className,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)

    // Check if there is content
    const hasContent = useMemo(() => value.trim().length > 0, [value])

    // Handle input changes
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        onChange?.(newValue)
      },
      [onChange]
    )

    // Handle close
    const handleClose = useCallback(() => {
      onClose?.()
    }, [onClose])

    // Handle focus state
    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        onFocus?.(e)
      },
      [onFocus]
    )

    // Handle blur state
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)
        onBlur?.(e)
      },
      [onBlur]
    )

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          handleClose()
        } else if (e.key === 'Enter') {
          e.preventDefault()
          onEnter?.()
        }
        onKeyDown?.(e)
      },
      [handleClose, onEnter, onKeyDown]
    )

    return (
      <div className={cn('flex items-center gap-1', className)}>
        <div className='relative flex-1'>
          <input
            ref={ref}
            type='text'
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn(
              // Base styles
              'h-9 w-full rounded-[12px]',
              'text-base font-["Geist"] leading-tight',
              'px-2 py-2',

              // Border styles
              'border transition-colors duration-200',

              // Text and placeholder colors
              'text-neutral-800',
              'placeholder:text-neutral-400',

              // Background and states
              'bg-white',

              // Focus state - similar to EmailInput
              isFocused
                ? 'focus:shadow focus:outline-none focus:border-[#846FE1]'
                : 'focus:outline-none',

              // Default border
              'border-gray-300',

              'shadow-[0_2px_4px_0_rgba(0,0,0,0.02),0_1px_6px_-1px_rgba(0,0,0,0.02),0_1px_2px_0_rgba(0,0,0,0.03)]',

              // Left padding for search icon
              'pl-8'
            )}
            {...props}
          />

          {/* Search icon */}
          <div className='absolute inset-y-0 left-0 flex items-center pl-[9.5px] pointer-events-none'>
            <svg
              width='17'
              height='17'
              viewBox='0 0 17 17'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M6.66667 1.66667C3.90524 1.66667 1.66667 3.90524 1.66667 6.66667C1.66667 9.42809 3.90524 11.6667 6.66667 11.6667C9.42809 11.6667 11.6667 9.42809 11.6667 6.66667C11.6667 3.90524 9.42809 1.66667 6.66667 1.66667ZM0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 8.20726 12.8108 9.6258 11.9332 10.7547L16.4226 15.2441L15.2441 16.4226L10.7547 11.9332C9.6258 12.8108 8.20726 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667Z'
                fill='#8E8E8E'
              />
            </svg>
          </div>
        </div>

        {/* Close button */}
        <button
          type='button'
          onClick={handleClose}
          className={cn(
            'w-[40px] h-[36px] px-3 flex items-center justify-center',
            'cursor-pointer hover:opacity-70 transition-opacity',
            'text-neutral-400 rounded-lg',
            'focus:bg-[rgba(0,0,0,0.06)] focus:outline-none'
          )}
          aria-label='Close search'
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M13.1404 1.82675C13.1407 1.82675 13.1411 1.82711 13.1418 1.82782L14.172 2.85818C14.1727 2.85872 14.1729 2.85907 14.1731 2.85961C14.1732 2.85996 14.1732 2.86033 14.1731 2.86068C14.1731 2.86122 14.1727 2.86157 14.172 2.86229L9.0345 7.99979L14.172 13.1373C14.1727 13.138 14.1729 13.1384 14.1731 13.1389C14.1732 13.1393 14.1732 13.1397 14.1731 13.1401C14.1731 13.1405 14.1727 13.1409 14.172 13.1416L13.1416 14.1718C13.1411 14.1725 13.1407 14.1726 13.1404 14.1728C13.14 14.1729 13.1395 14.1729 13.1391 14.1728C13.1386 14.1728 13.1382 14.1725 13.1375 14.1718L8.00003 9.03425L2.86253 14.1718C2.86182 14.1725 2.86146 14.1726 2.86093 14.1728C2.86052 14.1729 2.86008 14.1729 2.85968 14.1728C2.85932 14.1728 2.85896 14.1725 2.85825 14.1718L1.82807 13.1414C1.82735 13.1409 1.82718 13.1405 1.827 13.1401C1.82687 13.1397 1.82687 13.1393 1.827 13.1389C1.827 13.1384 1.82735 13.138 1.82807 13.1373L6.96557 7.99979L1.82807 2.86229C1.82735 2.86157 1.82718 2.86122 1.827 2.86068C1.82687 2.86027 1.82687 2.85984 1.827 2.85943C1.827 2.85907 1.82735 2.85872 1.82807 2.858L2.85843 1.82782C2.85896 1.82711 2.85932 1.82693 2.85968 1.82675C2.86008 1.82663 2.86052 1.82663 2.86093 1.82675C2.86146 1.82675 2.86182 1.82711 2.86253 1.82782L8.00003 6.96532L13.1375 1.82782C13.1382 1.82711 13.1386 1.82693 13.1391 1.82675C13.1395 1.82663 13.14 1.82663 13.1404 1.82675Z'
              fill='#8E8E8E'
            />
          </svg>
        </button>
      </div>
    )
  }
)

SidebarSearchInput.displayName = 'SidebarSearchInput'

export default SidebarSearchInput
